import type { BillData } from '@shared/schema/billData';
import { getDb, type BillRecord } from './database';

export type { BillRecord };

export async function createBill(input: {
  billData: BillData;
  imageThumbnail?: Blob;
  imageFull?: Blob;
  /** Defaults to 'ready' — callers only reach createBill after Review's confirm gate (validation + duplicate ack) has already passed. */
  submissionState?: BillRecord['submissionState'];
}): Promise<BillRecord> {
  const now = Date.now();
  const record: BillRecord = {
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
    billData: input.billData,
    submissionState: input.submissionState ?? 'ready',
    imageThumbnail: input.imageThumbnail,
    imageFull: input.imageFull,
  };
  const db = await getDb();
  await db.put('bills', record);
  return record;
}

export async function updateBill(
  id: string,
  updates: Partial<Pick<BillRecord, 'billData' | 'submissionState' | 'submittedAt'>>,
): Promise<BillRecord> {
  const db = await getDb();
  const existing = await db.get('bills', id);
  if (!existing) throw new Error(`Bill ${id} not found`);
  const updated: BillRecord = { ...existing, ...updates, updatedAt: Date.now() };
  await db.put('bills', updated);
  return updated;
}

export async function markSubmitted(id: string): Promise<BillRecord> {
  return updateBill(id, { submissionState: 'marked_submitted', submittedAt: Date.now() });
}

export async function getBill(id: string): Promise<BillRecord | null> {
  const db = await getDb();
  return (await db.get('bills', id)) ?? null;
}

/** Newest first. */
export async function listBills(): Promise<BillRecord[]> {
  const db = await getDb();
  const all = await db.getAllFromIndex('bills', 'by-createdAt');
  return all.reverse();
}

export async function deleteBill(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('bills', id);
}

/** Used by duplicate detection — queries the sellerPan index rather than scanning every bill. */
export async function findBySellerPan(sellerPan: string): Promise<BillRecord[]> {
  const db = await getDb();
  return db.getAllFromIndex('bills', 'by-sellerPan', sellerPan);
}
